/**
 * Colour parsing for the handful of places the renderer needs to reason about a colour rather
 * than just paint with it.
 *
 * Only one question is asked of it: is this fill opaque enough to hide what is under it? That
 * decides whether a shape counts as an occluder in the collision check, and getting it wrong in
 * either direction is costly — treat a tint as opaque and every layout with a translucent panel
 * fails the check; treat an opaque panel as a tint and a covered caption ships.
 */
import type { Color } from '@keymaker/schema';

/**
 * A fill at or above this alpha is treated as hiding what is behind it.
 *
 * Not 1, because a 90%-opaque panel visibly covers text and should be caught. Not 0.5, because
 * translucent tints and scrims are a normal part of these designs and a check that fires on all
 * of them gets switched off.
 */
export const OCCLUDES_ALPHA = 0.75;

/** Alpha of a hex or rgb()/rgba() colour. Returns 1 for anything it cannot parse. */
export function colorAlpha(color: Color | string): number {
  const value = color.trim();

  if (value.startsWith('#')) {
    const hex = value.slice(1);
    if (hex.length === 3 || hex.length === 4) {
      const alpha = hex.length === 4 ? hex[3]! : 'f';
      return parseInt(alpha + alpha, 16) / 255;
    }
    if (hex.length === 6 || hex.length === 8) {
      const alpha = hex.length === 8 ? hex.slice(6, 8) : 'ff';
      return parseInt(alpha, 16) / 255;
    }
    return 1;
  }

  const rgba = /^rgba?\(([^)]+)\)$/i.exec(value);
  if (rgba) {
    // Accepts every CSS form: `rgb(1,2,3)`, `rgba(1,2,3,.5)`, `rgb(1 2 3 / 50%)`,
    // `rgb(1 2 3 / 0.5)`. Splitting on whitespace as well as commas and the slash is what makes
    // the space-separated form work — a comma-only split silently reports it as opaque.
    const parts = rgba[1]!.split(/[\s,/]+/).filter((part) => part.length > 0);
    if (parts.length >= 4) {
      const alpha = parts[3]!;
      return alpha.endsWith('%') ? parseFloat(alpha) / 100 : parseFloat(alpha);
    }
    return 1;
  }

  return 1;
}

/** True when painting this colour at full strength would hide what is beneath it. */
export function hidesWhatIsUnderneath(color: Color | string): boolean {
  return colorAlpha(color) >= OCCLUDES_ALPHA;
}
